import { IconBook2, IconCalendar, IconCurrencyKroneSwedish, IconShoppingCart } from "@tabler/icons-react"
import { type FC, useState } from "react"

import { toPriceString } from "@forento/core/utilities/currency"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import Error from "~/components/error"
import Layout from "~/components/layout"
import Loader from "~/components/loader"
import { query } from "~/utilities/trpc"

const SalesPage: FC = () => {
	const [dateRange, setDateRange] = useState<{ startDate?: Date; endDate?: Date }>({})

	const sales = query.getSales.useQuery(Object.keys(dateRange).length > 0 ? dateRange : undefined)

	const totalRevenue = sales.data?.reduce((sum, course) => sum + course.totalRevenue, 0) ?? 0
	const totalSales = sales.data?.reduce((sum, course) => sum + course.totalSales, 0) ?? 0

	return (
		<Layout>
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-10">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h1 className="text-3xl font-semibold text-slate-900">Försäljning</h1>
						<p className="mt-2 text-slate-600">Översikt över försäljning för dina aktiva kurser</p>
					</div>

					<div className="flex gap-2">
						<div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
							<IconCalendar className="h-4 w-4 text-slate-500" />
							<input
								type="date"
								onChange={e =>
									setDateRange(c => ({
										...c,
										startDate: e.target.value ? new Date(e.target.value) : undefined,
									}))
								}
								className="text-sm outline-none"
								placeholder="Från datum"
							/>
						</div>
						<div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
							<IconCalendar className="h-4 w-4 text-slate-500" />
							<input
								type="date"
								onChange={e =>
									setDateRange(c => ({
										...c,
										endDate: e.target.value ? new Date(e.target.value) : undefined,
									}))
								}
								className="text-sm outline-none"
								placeholder="Till datum"
							/>
						</div>
					</div>
				</div>

				{sales.isLoading && (
					<div className="flex items-center justify-center py-20">
						<Loader label="Laddar försäljningsdata..." />
					</div>
				)}

				{sales.error && <Error message="Ett fel uppstod när försäljningsdata hämtades" />}

				{sales.data && (
					<>
						<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
							<SummaryCard
								title="Sålda kurser"
								value={totalSales.toString()}
								icon={IconShoppingCart}
								color="blue"
							/>
							<SummaryCard
								title="Totalt försäljningsvärde"
								value={toPriceString({ amount: totalRevenue, currency: "SEK" })}
								icon={IconCurrencyKroneSwedish}
								color="green"
							/>
							<SummaryCard
								title="Aktiva kurser"
								value={sales.data.length.toString()}
								icon={IconBook2}
								color="purple"
							/>
						</div>

						{sales.data.length === 0 ? (
							<div className="rounded-lg bg-slate-50 p-8 text-center">
								<p className="text-slate-600">Du har inga aktiva kurser med försäljning ännu.</p>
							</div>
						) : (
							<div className="space-y-8">
								{sales.data.map(course => (
									<CourseCard key={course.course.activeId} course={course} />
								))}
							</div>
						)}
					</>
				)}
			</div>
		</Layout>
	)
}

type SummaryCardProps = {
	title: string
	value: string
	icon: FC<{ className?: string }>
	color: "blue" | "green" | "purple"
}

const SummaryCard: FC<SummaryCardProps> = ({ title, value, icon: Icon, color }) => {
	const colorClasses = {
		blue: "bg-blue-50 text-blue-600",
		green: "bg-green-50 text-green-600",
		purple: "bg-purple-50 text-purple-600",
	}

	return (
		<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium text-slate-600">{title}</p>
					<p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
				</div>
				<div className={cn("rounded-lg p-3", colorClasses[color])}>
					<Icon className="h-6 w-6" />
				</div>
			</div>
		</div>
	)
}

type CourseData = {
	course: {
		activeId: number
		name: string
		thumbnailUrl: string | null
	}
	totalSales: number
	totalRevenue: number
	salesByMonth: {
		month: string
		sales: number
		revenue: number
	}[]
	recentOrders: {
		id: number
		date: Date
		customerName: string
		customerEmail: string
		amount: number
	}[]
}

const CourseCard: FC<{ course: CourseData }> = ({ course }) => {
	const recentMonths = course.salesByMonth.slice(-6)
	const maxRevenue = Math.max(...recentMonths.map(m => m.revenue), 1)

	return (
		<div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
			<div className="border-b border-slate-200 p-6">
				<div className="flex items-start gap-4">
					{course.course.thumbnailUrl && (
						<img
							src={course.course.thumbnailUrl}
							alt={course.course.name}
							className="h-16 w-16 rounded-lg object-cover"
						/>
					)}
					<div className="flex-1">
						<h3 className="text-lg font-semibold text-slate-900">{course.course.name}</h3>
						<div className="mt-2 flex gap-6 text-sm">
							<div>
								<span className="text-slate-600">Sålda exemplar:</span>{" "}
								<span className="font-medium text-slate-900">{toNumberFormat(course.totalSales)}</span>
							</div>
							<div>
								<span className="text-slate-600">Försäljningsvärde:</span>{" "}
								<span className="font-medium text-slate-900">
									{toPriceString({ amount: course.totalRevenue, currency: "SEK" })}
								</span>
							</div>
						</div>
					</div>
				</div>

				{recentMonths.length > 0 && (
					<div className="mt-6">
						<h4 className="mb-8 text-sm font-medium text-slate-700">Månadsvis försäljningsvärde</h4>
						<div className="flex items-end justify-between gap-2">
							{recentMonths.map(month => (
								<div key={month.month} className="flex flex-1 flex-col items-center">
									<div className="relative w-full">
										<div
											className="bg-primary/20 w-full rounded-t"
											style={{
												height: `${Math.max((month.revenue / maxRevenue) * 120, 4)}px`,
											}}
										/>
										<div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium text-slate-700">
											{toPriceString({ amount: month.revenue, currency: "SEK" })}
										</div>
									</div>
									<p className="mt-1 text-xs text-slate-500">
										{new Date(month.month + "-01").toLocaleDateString("sv-SE", { month: "short" })}
									</p>
								</div>
							))}
						</div>
					</div>
				)}
			</div>

			{course.recentOrders.length > 0 && (
				<div className="p-6">
					<h4 className="mb-4 text-sm font-medium text-slate-700">Senaste beställningar</h4>
					<div className="space-y-3">
						{course.recentOrders.map(order => (
							<div key={order.id} className="flex items-center justify-between text-sm">
								<div className="flex-1">
									<p className="font-medium text-slate-900">{order.customerName}</p>
									<p className="text-slate-600">{order.customerEmail}</p>
								</div>
								<div className="text-right">
									<p className="font-medium text-slate-900">
										{toPriceString({ amount: order.amount, currency: "SEK" })}
									</p>
									<p className="text-slate-500">{new Date(order.date).toLocaleDateString("sv-SE")}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	)
}

export default SalesPage
